import React, { useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { fetchServerPlayerProfile } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import { formattedDate } from "src/helpers/DateHelpers";

import { Drawer, Spin, Tag } from 'antd';
import { playtimeHours, shortFromNow } from "./_serverHelpers";

interface PlayerProfile {
  name: string;
  xuid: string | null;
  online: boolean;
  firstSeen: string | null;
  lastSeen: string | null;
  sessions: number;
  playtimeSeconds: number;
  banned: { reason: string | null, at: string } | null;
  inAllowlist: boolean;
}

// Everything crafty knows about one player on this server, opened from
// any list that shows a gamertag (roster, online now, bans). A null
// gamertag keeps the drawer closed.
const _PlayerProfile = ({ server, gamertag, onClose }: {
  server: ServerInterface | undefined,
  gamertag: string | null,
  onClose: () => void
}) => {
  const dispatch = useAppDispatch();

  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const serverId = server?.id;

  useEffect(() => {
    if (!serverId || !gamertag) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setProfile(null);
      setError(null);

      const resultAction = await dispatch(fetchServerPlayerProfile({ id: serverId, gamertag }));
      if (cancelled) return;

      setLoading(false);
      if (fetchServerPlayerProfile.fulfilled.match(resultAction)) {
        setProfile(resultAction.payload);
      } else {
        const payload: any = resultAction.payload;
        setError(Array.isArray(payload) ? payload[0] : 'Could not load the profile');
      }
    })().catch(console.error);

    return () => { cancelled = true; };
  }, [serverId, gamertag, dispatch]);

  const seen = (iso: string | null) =>
    iso ? `${shortFromNow(iso)} (${formattedDate(iso).dateTime})` : 'never';

  return (
    <Drawer
      title={gamertag || 'Player'}
      open={!!gamertag}
      onClose={onClose}
      width={400}
      className='v-srv-profile'
    >
      {loading && <div className='o-loading'><Spin /></div>}

      {error && !loading && <div className='o-error'>{error}</div>}

      {profile && !loading &&
        <div className='o-rows'>
          <div className='o-row'>
            <span className='o-label'>Status</span>
            <span className='o-value'>
              {profile.online
                ? <Tag color='green'>online</Tag>
                : <Tag>offline</Tag>}
              {profile.banned && <Tag color='red'>banned</Tag>}
              {profile.inAllowlist && <Tag color='blue'>allowlisted</Tag>}
            </span>
          </div>

          {profile.banned &&
            <div className='o-row'>
              <span className='o-label'>Ban</span>
              <span className='o-value'>
                {profile.banned.reason || 'no reason recorded'}
                <span className='o-sub'> — {shortFromNow(profile.banned.at)}</span>
              </span>
            </div>
          }

          <div className='o-row'>
            <span className='o-label'>Playtime</span>
            <span className='o-value'>{playtimeHours(profile.playtimeSeconds)}</span>
          </div>

          <div className='o-row'>
            <span className='o-label'>Sessions</span>
            <span className='o-value'>{profile.sessions}</span>
          </div>

          <div className='o-row'>
            <span className='o-label'>First seen</span>
            <span className='o-value'>{seen(profile.firstSeen)}</span>
          </div>

          <div className='o-row'>
            <span className='o-label'>Last seen</span>
            <span className='o-value'>{seen(profile.lastSeen)}</span>
          </div>

          {profile.xuid &&
            <div className='o-row'>
              <span className='o-label'>XUID</span>
              <span className='o-value o-mono'>{profile.xuid}</span>
            </div>
          }
        </div>
      }
    </Drawer>
  );
}

export default _PlayerProfile;
